import { WarningFilled } from '@ant-design/icons'
import { useSiteMonitoring } from '@api/useSiteMonitoring'
import { useSiteMonitoringContext } from '@components/StoreMonitoring/SiteMonitoringContext'
import { GraviGrid, Horizontal, Texto, MomentDatePicker } from '@gravitate-js/excalibrr'
import { useLocalStorage } from '@gravitate-js/the-armory'
import { useModelConfig } from '@hooks/useModelConfig'
import { formatters } from '@utils/intl/dateTimeFormatters'
import moment from 'moment'
import { useCallback, useMemo, useRef } from 'react'

import { ComponentPrices } from '@components/_SiteWide/OrderDisplay/ComponentPrices'

import { useEasyOrderMovements } from '../EasyOrderMovements/MovementStoreProvider'
import {
  MovementAction,
  MovementStatus,
  ProductsRenderer,
  ShowOrderModalRenderer,
  WindowTimeRenderer,
} from './cellRenderers'
import { CarrierNameCellRenderer } from '@/pages/OrderManagement/components/Grid/Columns/OrderColumnCellrenderers'

const { RangePicker } = MomentDatePicker

export function SiteDeliveriesGrid({ store }) {
  const gridAPIRef = useRef()
  const { useSiteViewOrdersQuery } = useSiteMonitoring()
  const { showModal } = useSiteMonitoringContext()
  const { config } = useModelConfig()
  const requiresValidPrice = config?.attendant_validation?.require_valid_price
  const gridParent = useRef()
  const { setActiveOrder } = useEasyOrderMovements()
  const { value: attendantDayRange, setValue: setAttendantDayRange } = useLocalStorage('attendant-day-range', {
    start_date: moment().subtract(6, 'days').startOf('day'),
    end_date: moment().endOf('day'),
  })

  const { data: orders, isFetching } = useSiteViewOrdersQuery(
    store?.id,
    moment(attendantDayRange?.start_date)
      .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
      .format('YYYY-MM-DD HH:mm:ss'),
    moment(attendantDayRange?.end_date)
      .set({ hour: 23, minute: 59, second: 0, millisecond: 0 })
      .format('YYYY-MM-DD HH:mm:ss')
  )
  // *** Temporarily disabling row pinning: ***
  // useEffect(() => {
  //   if (orders) {
  //     gridAPIRef.current?.setPinnedTopRowData(
  //       orders.filter((order) => order.needs_attention_reason === 'past_delivery')
  //     )
  //   }
  // }, [orders])
  const getRowId = useCallback((row) => row?.data?.id, [])
  const getRowStyle = useCallback((params) => {
    if (params.data?.needs_attention_reason === 'past_delivery') {
      return { background: 'var(--theme-warning-dim)' }
    }
  }, [])
  const agPropOverrides = useMemo(
    () => ({
      rowGroupPanelShow: 'never',
      sideBar: null,
      columnDefaultOverrides: {
        resizeable: false,
        flex: undefined,
      },
      getRowStyle,
      getRowId,
    }),
    []
  )
  const columnDefs = useMemo(() => getColumnDefs(showModal, setActiveOrder, requiresValidPrice), [config])
  const needsAttentionOrderCount = useMemo(() => {
    return orders?.filter((order) => ['needs_bols', 'past_delivery'].includes(order.needs_attention_reason)).length
  }, [orders])

  return (
    <div className='site-attendant-view' ref={gridParent} style={{ height: '93%' }}>
      <Horizontal justifyContent='end'>
        <RangePicker
          allowClear={false}
          format={formatters.DATE}
          defaultValue={[moment(attendantDayRange.start_date), moment(attendantDayRange.end_date)]}
          onChange={(dates) => {
            setAttendantDayRange({
              start_date: dates[0],
              end_date: dates[1],
            })
          }}
        />
      </Horizontal>
      {needsAttentionOrderCount > 0 && (
        <Horizontal className='p-2 bg-warning' style={{ borderRadius: '4px 4px 0 0 ' }}>
          <Texto category='h6' appearance='white'>
            <WarningFilled className='mr-2 ml-2' /> You have {needsAttentionOrderCount} deliveries that need attention.
          </Texto>
        </Horizontal>
      )}

      <GraviGrid
        groupDefaultExpanded={-1}
        externalRef={gridAPIRef}
        rowData={orders}
        loading={isFetching}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        rowHeight='auto'
        onFirstDataRendered={(params) => {
          // *** Temporarily disabling row pinning: ***
          // params.api.setPinnedTopRowData(
          //   orders
          //     ?.filter((order) => order.needs_attention_reason === 'past_delivery')
          //     .sort((a, b) => (a.carrier_window_start > b.carrier_window_start ? 1 : -1))
          // )
          // params.api.sizeColumnsToFit()
          // params.api.sizeRowsToFit()
        }}
        storageKey='site-deliveries-grid'
      />
    </div>
  )
}

const getColumnDefs = (showModal, handleDeliveryModalOpen, requiresValidPrice) => [
  {
    field: 'show_order_modal',
    headerName: '',
    filter: false,
    sortable: false,
    flex: 0,
    minWidth: 75,
    cellRenderer: ShowOrderModalRenderer,
    cellRendererParams: {
      showModal,
    },
  },
  {
    field: 'po',
    headerName: 'PO',
    initialHide: false,
    flex: 0,
    width: 140,
    filter: false,
  },
  {
    field: 'movement_status',
    headerName: 'Movement Status',
    flex: 0,
    cellRenderer: MovementStatus,
    // These value getter cases are just for the display of the groupings on the grid.
    // Refer to the cellRenderer for actual column display output
    valueGetter: (params) => {
      if (params.data.movement_status === 'delivered') return 'Needs Delivery Info'
      if (params.data.movement_status === 'needs_review') return 'Needs Review'
      if (params.data.movement_status === 'missing_price') return 'Missing Price'
      return params.data.movement_status
    },
    rowGroup: true,
    sort: 'desc',
    sortIndex: 1,
    filter: false,
    minWidth: 200,
    comparator: (a, b) => {
      const statusOrder = ['needs delivery info', 'needs review', 'scheduled', 'missing price', 'completed'].reverse()
      const aStatusIndex = statusOrder.indexOf(a?.toLowerCase())
      const bStatusIndex = statusOrder.indexOf(b?.toLowerCase())
      return aStatusIndex - bStatusIndex
    },
  },
  {
    field: 'dispatch_window_start',
    headerName: 'Delivery Date',
    filter: false,
    initialHide: true,
    valueGetter: ({ data }) => {
      if (!data) return
      if (data.actual_end) {
        return formatters.WEEKDAY_MONTH_DATE(moment(data.actual_end))
      }
      if (data.carrier) {
        return formatters.WEEKDAY_MONTH_DATE(moment(data.carrier_window_start))
      }
      return formatters.WEEKDAY_MONTH_DATE(moment(data.dispatch_window_start))
    },
    cellRenderer: ({ value }) => <Texto weight='bold'>{value}</Texto>,
  },
  {
    field: 'dispatch_window',
    headerName: 'Carrier Window',
    filter: false,
    cellRenderer: WindowTimeRenderer,
    minWidth: 140,
    valueGetter: ({ data }) => {
      if (!data) return
      if (data.carrier) {
        return `${formatters.MONTH_DATE_TIME(moment(data.carrier_window_start))} - ${formatters.MONTH_DATE_TIME(
          moment(data.carrier_window_end)
        )}`
      }
      return `${formatters.MONTH_DATE_TIME(moment(data.dispatch_window_start))} - ${formatters.MONTH_DATE_TIME(
        moment(data.dispatch_window_end)
      )}`
    },
    comparator: (_a, _b, a, b) => {
      const retain1 = a?.data?.carrier ? a?.data?.carrier_window_start : a?.data?.dispatch_window_start
      const retain2 = b?.data?.carrier ? b?.data?.carrier_window_start : b?.data?.dispatch_window_start

      return retain1 > retain2 ? 1 : -1
    },
    sort: 'asc',
    sortIndex: 2,
  },
  {
    field: 'carrier',
    headerName: 'Carrier',
    filter: false,
    flex: 1,
    minWidth: 150,
    cellRenderer: CarrierNameCellRenderer,
  },
  {
    field: 'products',
    headerName: 'Products',
    filter: false,
    autoHeight: true,
    cellRenderer: ProductsRenderer,
    comparator: (_a, _b, a, b) => a?.data?.product_ratios[0]?.name?.localeCompare(b?.data?.product_ratios[0]?.name),
  },
  {
    field: 'component_paths',
    headerName: 'Components',
    minWidth: 200,
    filter: false,
    autoHeight: true,
    initialHide: true,
    cellRenderer: ComponentPrices,
    cellRendererParams: {
      requiresValidPrice,
    },
    comparator: (_a, _b, a, b) =>
      a?.data?.component_paths[0]?.component_product_name?.localeCompare(
        b?.data?.component_paths[0]?.component_product_name
      ),
  },
  {
    headerName: 'Driver Notes',
    field: 'driver_note',
    initialHide: true,
    suppressFiltersToolPanel: true,
  },
  {
    field: '',
    headerName: 'Actions',
    minWidth: 220,
    flex: 0,
    filter: false,
    cellRenderer: MovementAction,
    cellRendererParams: {
      handleDeliveryModalOpen,
    },
    pinned: 'right',
  },
]
